import { Injectable } from '@angular/core'
import { environment } from '../../environments/environment'
import { isSomething, selectSomeOnce, User } from '@tokeet-frontend/tv3-platform'
import { Store } from '@ngrx/store'
import { selectActivePlanStatusView } from '@tv3/store/plan/plan.selectors'
import { userRoleToString } from '@tv3/utils/functions/user-role-to-string'
import moment from 'moment'
import * as R from 'ramda'
import * as amplitude from '@amplitude/unified'
import { Identify } from '@amplitude/analytics-core'

@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  private initialized = false

  constructor(private store: Store<any>) {}

  init() {
    if (this.initialized || !environment.config.amplitudeApiKey) {
      return
    }

    try {
      amplitude.initAll(environment.config.amplitudeApiKey, {
        analytics: {
          autocapture: false,
          // {
          // attribution: true,
          // fileDownloads: true,
          // formInteractions: true,
          // pageViews: true,
          // sessions: true,
          // elementInteractions: true,
          // networkTracking: true,
          // webVitals: true,
          // frustrationInteractions: true,
          // }
        },
      })
      this.initialized = true
    } catch (e) {
      console.error('Error initializing Amplitude', e)
    }
  }

  logEvent(event: string) {
    if (!this.initialized) {
      console.log('Amplitude event (not initialized):', event)
      return
    }

    try {
      amplitude.track(event)
    } catch (e) {
      console.error('Error logging event to Amplitude', e)
    }
  }

  logPageView() {
    if (!this.initialized) {
      return
    }

    try {
      amplitude.track('pageview', { url: window.location.pathname })
    } catch (e) {
      console.error('Error logging page view to Amplitude', e)
    }
  }

  setUser(user: User) {
    if (!this.initialized || !isSomething(user)) {
      return
    }

    try {
      amplitude.setUserId(user.id.toString())

      this.store.pipe(selectSomeOnce(selectActivePlanStatusView)).subscribe((status) => {
        try {
          const identifyEvent = new Identify()
          identifyEvent.set('user_id', user.id)
          identifyEvent.set('user_email', user.primaryEmail)
          identifyEvent.set('user_name', `${user.firstName} ${user.lastName}`)
          identifyEvent.set('user_phone', user.phone)
          identifyEvent.set('user_role', userRoleToString(user.roles))
          identifyEvent.set('user_created_at', user.created)
          identifyEvent.set('account', user.account)
          identifyEvent.set('subscription', status?.planName)
          identifyEvent.set(
            'subscription_status',
            status.status === 'inactive' ? (status.delinquent ? 'past_due' : 'inactive') : status.status
          )
          identifyEvent.set('subscription_cycle_start_at', status.start)
          identifyEvent.set('subscription_cycle_end_at', status.until)
          identifyEvent.set('subscription_updated_at', status.timestamp)
          identifyEvent.set('trial', status?.trial_end > moment().unix())
          identifyEvent.set('trial_end_at', status.trial_end)
          identifyEvent.set('rental_count', R.pathOr(0, ['rentals'], status))
          identifyEvent.set('user_count', R.pathOr(0, ['users'], status))
          identifyEvent.set('platform', 'acm')

          amplitude.identify(identifyEvent)
        } catch (e) {
          console.error('Error identifying user in Amplitude', e)
        }
      })
    } catch (e) {
      console.error('Error setting user in Amplitude', e)
    }
  }

  logOut() {
    if (!this.initialized) {
      return
    }

    try {
      amplitude.reset()
    } catch (e) {
      console.error('Error logging out from Amplitude', e)
    }
  }
}
